import { create } from 'zustand';
import type { ModalTypes } from '@/components/ModalRenderer';
import type { ModalPayload } from '@/components/ModalRenderer/modalMap';
import { MODAL_CLOSE_TIMEOUT } from '@/constants/constants';
import { environment } from '@/utils/env.utils';

type ModalOptionsBase = {
  isPersistedFromRedirect?: boolean;
  confirmAction?: () => void;
  closeAction?: () => void;
};

type ModalOptions<T extends ModalTypes = ModalTypes> = ModalOptionsBase & {
  payload?: ModalPayload<T>;
};

type IsOmittablePayload<P> = [undefined] extends [P] ? true : Partial<P> extends P ? true : false;

type OpenModalArgs<T extends ModalTypes> =
  IsOmittablePayload<ModalPayload<T>> extends true
    ? [type: T, options?: ModalOptionsBase & { payload?: ModalPayload<T> }]
    : [type: T, options: ModalOptionsBase & { payload: ModalPayload<T> }];

export type OpenModal = <T extends ModalTypes>(...args: OpenModalArgs<T>) => void;

type ModalItem = {
  [T in ModalTypes]: {
    type: T;
    options: ModalOptions<T>;
  };
}[ModalTypes];

type ModalState = {
  currentModal: ModalItem | null;
  nextModal: ModalItem | null;
  status: 'idle' | 'closing';

  openModal: OpenModal;
  closeModal: () => void;
  closeNonPersistedModal: () => void;
  reset: () => void;
  advanceModal: () => void;
};

const initialState = {
  currentModal: null,
  nextModal: null,
  status: 'idle',
} satisfies Partial<ModalState>;

export const useModalStore = create<ModalState>()((set, get) => ({
  ...initialState,
  closeModal: () => {
    const { status, currentModal, advanceModal } = get();

    // If a modal is open, close it
    if (status === 'idle' && currentModal) {
      set({ status: 'closing' });

      // Animations are disabled in tests, so we don't need the timeout
      if (environment.isTesting) {
        advanceModal();
      } else {
        // Wait for the animation to finish before advancing to the next modal
        setTimeout(advanceModal, MODAL_CLOSE_TIMEOUT);
      }
    }
  },
  openModal: (type, options = {}) => {
    const modal = { type, options } as ModalItem;
    const { status, currentModal, closeModal } = get();

    // If a modal is not open, update the current modal
    if (status === 'idle' && !currentModal) {
      set({ currentModal: modal });
      return;
    }

    set({ nextModal: modal });
    closeModal();
  },
  closeNonPersistedModal: () => {
    const { status, currentModal, closeModal } = get();

    if (status === 'idle' && !currentModal?.options.isPersistedFromRedirect) {
      closeModal();
    }
  },
  advanceModal: () => set(prev => ({ currentModal: prev.nextModal, nextModal: null, status: 'idle' })),
  reset: () => set(initialState),
}));
